<template>
    <div>

        <fieldset>
            <legend>腔体参数</legend>
            <div class="param-row">
                <label>类型:</label>
                <select v-model="cavityType" style="width:150px">
                    <option value="circular">圆内腔</option>
                    <option value="square">方内腔</option>
                </select>
            </div>
            <div class="param-row" v-show="cavityType === 'circular'">
                <label>直径:</label>
                <NumberInput v-model="diameter" style="width:100px" />
                <span class="hint">mm</span>
            </div>
            <div class="param-row" v-show="cavityType === 'square'">
                <label>长度:</label>
                <NumberInput v-model="length" style="width:100px" />
                <span class="hint">mm</span>
            </div>
            <div class="param-row" v-show="cavityType === 'square'">
                <label>宽度:</label>
                <NumberInput v-model="width" style="width:100px" />
                <span class="hint">mm</span>
            </div>
            <div class="param-row">
                <label>深度:</label>
                <NumberInput v-model="stroke" style="width:100px" />
                <span class="hint">mm</span>
            </div>
        </fieldset>

        <fieldset>
            <legend>螺旋</legend>
            <div class="param-row">
                <label>方式:</label>
                <label style="width:auto"><input v-model="descentMode" type="radio" value="layerHeight"> 层高</label>
                <label style="width:auto"><input v-model="descentMode" type="radio" value="slope"> 坡度</label>
            </div>
            <div class="param-row" v-show="descentMode === 'layerHeight'">
                <label>每层高度:</label>
                <NumberInput v-model="layerHeight" style="width:80px" />
                <span class="hint">mm</span>
            </div>
            <div class="param-row" v-show="descentMode === 'slope'">
                <label>坡度:</label>
                <NumberInput v-model="slopeAngle" style="width:80px" />
                <span class="hint">°</span>
            </div>
            <div class="param-row">
                <label>{{ descentMode === 'layerHeight' ? '换算坡度:' : '每转下降高度:' }}</label>
                <span>{{ descentMode === 'layerHeight' ? actualSlopeAngle.toFixed(2) + '°' : actualHeightPerRev.toFixed(4) + ' mm' }}</span>
            </div>
            <div class="param-row">
                <label>预计转数:</label>
                <span>{{ estimatedRevs }}</span>
            </div>
            <div class="param-row" v-show="false">
                <label>弦长:</label>
                <NumberInput v-model="chordLength" style="width:80px" />
                <span class="hint">mm (G1逼近用，当前G3模式)</span>
            </div>
            <div class="param-row" v-show="false">
                <label>分段角度:</label>
                <span>{{ segmentAngle.toFixed(2) }}°</span>
            </div>
            <div class="param-row" v-show="false">
                <label>G3精修间隔:</label>
                <NumberInput v-model="arcInterval" style="width:80px" />
                <span class="hint">mm (当前每层均为G3)</span>
            </div>
        </fieldset>

        <fieldset>
            <legend>刀具与加工参数</legend>
            <div class="param-row">
                <label>主轴转速:</label>
                <NumberInput v-model="spindleRPM" style="width:100px" />
                <span class="hint">RPM</span>
            </div>
            <div class="param-row">
                <label>进给:</label>
                <NumberInput v-model="feed" style="width:100px" />
                <span class="hint">mm/min</span>
            </div>
            <div class="param-row">
                <label>刀具直径:</label>
                <NumberInput v-model="toolDiameter" style="width:100px" />
                <span class="hint">mm</span>
            </div>
            <div class="param-row">
                <label>吃刀量(%):</label>
                <NumberInput v-model="cutDepthPercent" style="width:80px" />
                <span class="hint">= {{ actualCutDepth.toFixed(2) }}mm (刀具直径%)</span>
            </div>
            <div class="param-row">
                <label>顶偏置距离:</label>
                <NumberInput v-model="topOffset" style="width:80px" />
                <span class="hint">mm (默认1mm，Z=该值时开始加工)</span>
            </div>
            <div class="param-row">
                <label>安全距离:</label>
                <NumberInput v-model="safeZ" style="width:80px" />
                <span class="hint">mm (Z安全高度，对刀后停止位置)</span>
            </div>
            <div class="param-row" v-show="cavityType === 'square'">
                <label>角半径:</label>
                <NumberInput v-model="cornerRadius" style="width:80px" />
                <span class="hint">mm (默认0)</span>
            </div>
            <div class="param-row">
                <span class="hint">{{ flatMillInfo }}</span>
            </div>
        </fieldset>

        <fieldset>
            <legend>预设</legend>
            <div class="param-row">
                <select v-model="selectedPreset" style="width:160px">
                    <option value="">-- 选择预设 --</option>
                    <option v-for="(p, i) in presets" :value="i" :key="i">{{ p.title }}</option>
                </select>
                <button @click="applyPreset" style="margin-left:4px">应用</button>
            </div>
        </fieldset>

        <fieldset>
            <legend>生成</legend>
            <button @click="generateNC">后处理NC文件</button>
        </fieldset>

    </div>
</template>

<script>
import { inject, ref } from "vue";
import NumberInput from "./NumberInput.vue";

export default {
    components: { NumberInput },

    setup() {
        const fileManager = inject("fileManager");

        // 腔体类型
        const cavityType = ref("circular");

        // 腔体尺寸
        const diameter = ref(10);
        const length = ref(30);
        const width = ref(20);

        // 坡度与层高
        const descentMode = ref("layerHeight");  // "layerHeight" | "slope"
        const layerHeight = ref(0.1);
        const slopeAngle = ref(1);
        const stroke = ref(2);

        // 刀具与加工参数
        const spindleRPM = ref(6000);
        const feed = ref(1000);
        const toolDiameter = ref(6);
        const cutDepthPercent = ref(60);
        const chordLength = ref(0.5);
        const arcInterval = ref(3);
        const topOffset = ref(1);
        const safeZ = ref(10);
        const cornerRadius = ref(0);

        // 预设参数表（按 ISO 10642 沉头螺钉标准，仅定义常用项，空缺参数不覆盖）
        const presets = [
            { title: "M2沉头",  cavityType: "circular", diameter: 4.5,  stroke: 2.5, toolDiameter: 3,  spindleRPM: 6000, feed: 1000,  layerHeight: 0.15 },
            { title: "M2.5沉头", cavityType: "circular", diameter: 5.0,  stroke: 3, toolDiameter: 4,  spindleRPM: 6000, feed: 1000,  layerHeight: 0.15 },
            { title: "M3沉头",  cavityType: "circular", diameter: 6.5,  stroke: 3.5, toolDiameter: 4,  spindleRPM: 6000, feed: 1000, layerHeight: 0.15 },
            { title: "M4沉头",  cavityType: "circular", diameter: 8,  stroke: 4.5, toolDiameter: 6,  spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
            { title: "M5沉头",  cavityType: "circular", diameter: 9.5, stroke: 5.5, toolDiameter: 6,  spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
            { title: "M6沉头",  cavityType: "circular", diameter: 11.0, stroke: 6.5, toolDiameter: 6,  spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
            { title: "M8沉头",  cavityType: "circular", diameter: 14.0, stroke: 8.5,  toolDiameter: 6, spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
            { title: "M10沉头", cavityType: "circular", diameter: 16.5, stroke: 10.5, toolDiameter: 6, spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
            { title: "M12沉头", cavityType: "circular", diameter: 19, stroke: 12.5, toolDiameter: 6, spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
            { title: "M14沉头", cavityType: "circular", diameter: 22, stroke: 14.5, toolDiameter: 6, spindleRPM: 5000, feed: 1800, layerHeight: 0.25 },
            { title: "M16沉头", cavityType: "circular", diameter: 25.0, stroke: 16.5, toolDiameter: 6, spindleRPM: 5000, feed: 1000, layerHeight: 0.15 },
        ];
        const selectedPreset = ref("");

        return {
            fileManager,
            cavityType,
            diameter, length, width,
            descentMode, layerHeight, slopeAngle, stroke,
            spindleRPM, feed, toolDiameter,
            cutDepthPercent, chordLength, arcInterval, topOffset, safeZ, cornerRadius,
            presets, selectedPreset,
        };
    },

    computed: {
        // 实际吃刀量
        actualCutDepth() {
            return this.toolDiameter * this.cutDepthPercent / 100;
        },

        // 腔体周长（刀具中心走一圈的实际路径长度）
        perimeter() {
            if (this.cavityType === "circular") {
                return Math.PI * this.diameter;
            } else {
                // 方腔：直边 + 圆角弧长
                // 4条直边各减去两端的圆角半径，4个圆角各为1/4圆弧
                const straight = 2 * (this.length + this.width) - 8 * this.cornerRadius;
                const arcs = 2 * Math.PI * this.cornerRadius;
                return straight + arcs;
            }
        },

        // 实际每转下降高度（取决于模式）
        actualHeightPerRev() {
            if (this.descentMode === "layerHeight") {
                return this.layerHeight;
            }
            const rad = this.slopeAngle * Math.PI / 180;
            return this.perimeter * Math.tan(rad);
        },

        // 实际坡度（取决于模式）
        actualSlopeAngle() {
            if (this.descentMode === "slope") {
                return this.slopeAngle;
            }
            if (this.perimeter <= 0) return 0;
            const rad = Math.atan(this.layerHeight / this.perimeter);
            return rad * 180 / Math.PI;
        },

        // 分段角度（圆形G1逼近的每段圆心角）
        segmentAngle() {
            if (this.cavityType !== "circular") return 0;
            const toolR = this.toolDiameter / 2;
            const R = this.diameter / 2 - toolR;
            if (R <= 0 || this.chordLength <= 0) return 0;
            const half = Math.asin(Math.min(this.chordLength / (2 * R), 1));
            return half * 2 * 180 / Math.PI;
        },

        // 预计转数
        estimatedRevs() {
            if (this.actualHeightPerRev <= 0) return 0;
            return Math.ceil(this.stroke / this.actualHeightPerRev);
        },

        // 是否需要平面铣
        needsFlatMill() {
            const threshold = 2 * this.toolDiameter - 0.5;
            if (this.cavityType === "circular") {
                return this.diameter > threshold;
            } else {
                return this.length > threshold && this.width > threshold;
            }
        },

        // 平铣提示信息
        flatMillInfo() {
            if (this.needsFlatMill) {
                return "腔体较大，每层将进行底面平铣 (回旋方式, 步进=" + this.actualCutDepth.toFixed(2) + "mm)";
            }
            const threshold = (2 * this.toolDiameter - 0.5).toFixed(1);
            return "腔体尺寸 ≤ " + threshold + "mm，无需底面平铣";
        },
    },

    methods: {
        // 应用预设参数：仅覆盖预设中定义的字段，空缺参数保留当前值
        applyPreset() {
            if (this.selectedPreset === "") return;
            const p = this.presets[this.selectedPreset];
            const fields = [
                'cavityType', 'diameter', 'length', 'width', 'stroke',
                'descentMode', 'layerHeight', 'slopeAngle',
                'spindleRPM', 'feed', 'toolDiameter', 'cutDepthPercent',
                'topOffset', 'safeZ', 'cornerRadius'
            ];
            for (const key of fields) {
                if (p[key] !== undefined) {
                    this[key] = p[key];
                }
            }
        },

        // 根据半径和设定弦长计算分段数
        circleSegments(R) {
            if (R <= 0) return 36;
            const chord = Math.min(this.chordLength, R * 2);
            const halfAngle = Math.asin(chord / (2 * R));
            return Math.max(8, Math.ceil(Math.PI / halfAngle));
        },

        // 圆形螺旋下降一圈（4×90° G3 含Z，真正螺旋，G91相对坐标）
        generateCircularHelical(fromZ, toZ) {
            const toolR = this.toolDiameter / 2;
            const R = this.diameter / 2 - toolR;
            const dzTotal = toZ - fromZ;  // 负值，表示下降
            const dq = dzTotal / 4;        // 每段1/4弧的Z增量
            let nc = "";
            // 弧1: (R,0) → (0,R), CCW, 圆心(0,0)
            nc += `G3 X${(-R).toFixed(3)} Y${R.toFixed(3)} Z${dq.toFixed(3)} I${(-R).toFixed(3)} J0\n`;
            // 弧2: (0,R) → (-R,0)
            nc += `G3 X${(-R).toFixed(3)} Y${(-R).toFixed(3)} Z${dq.toFixed(3)} I0 J${(-R).toFixed(3)}\n`;
            // 弧3: (-R,0) → (0,-R)
            nc += `G3 X${R.toFixed(3)} Y${(-R).toFixed(3)} Z${dq.toFixed(3)} I${R.toFixed(3)} J0\n`;
            // 弧4: (0,-R) → (R,0)
            nc += `G3 X${R.toFixed(3)} Y${R.toFixed(3)} Z${dq.toFixed(3)} I0 J${R.toFixed(3)}\n`;
            return nc;
        },

        // G3精修整圆（4×90°弧，恒定Z，G91相对坐标）
        generateCircularCleanup() {
            const toolR = this.toolDiameter / 2;
            const R = this.diameter / 2 - toolR;
            let nc = "";
            // 弧1: (R,0) → (0,R), CCW
            nc += `G3 X${(-R).toFixed(3)} Y${R.toFixed(3)} I${(-R).toFixed(3)} J0\n`;
            // 弧2: (0,R) → (-R,0)
            nc += `G3 X${(-R).toFixed(3)} Y${(-R).toFixed(3)} I0 J${(-R).toFixed(3)}\n`;
            // 弧3: (-R,0) → (0,-R)
            nc += `G3 X${R.toFixed(3)} Y${(-R).toFixed(3)} I${R.toFixed(3)} J0\n`;
            // 弧4: (0,-R) → (R,0)
            nc += `G3 X${R.toFixed(3)} Y${R.toFixed(3)} I0 J${R.toFixed(3)}\n`;
            return nc;
        },

        // 方形螺旋下降一圈（CCW顺铣，G91相对坐标）
        // skipPositioning: 为true时跳过起始定位移动（用于清根，此时刀具已在路径起点）
        generateSquareHelical(fromZ, toZ, skipPositioning = false) {
            const toolR = this.toolDiameter / 2;
            const Rc = this.cornerRadius;

            // 刀具中心路径边界（绝对坐标，用于计算增量）
            const x1 = -this.length / 2 + toolR;
            const x2 = this.length / 2 - toolR;
            const y1 = -this.width / 2 + toolR;
            const y2 = this.width / 2 - toolR;

            const totalDescent = toZ - fromZ;
            let nc = "";

            if (Rc > 0) {
                // 切点参数 t = 2 * sqrt(Rc * toolR)
                const t = 2 * Math.sqrt(Rc * toolR);

                // 四个角心
                const cxTL = -this.length / 2 + Rc;  const cyTL = this.width / 2 - Rc;
                const cxTR = this.length / 2 - Rc;   const cyTR = this.width / 2 - Rc;
                const cxBR = this.length / 2 - Rc;   const cyBR = -this.width / 2 + Rc;
                const cxBL = -this.length / 2 + Rc;  const cyBL = -this.width / 2 + Rc;

                // 各角与直边的切点
                const yTL_left = cyTL + t;  const xTL_top = cxTL + t;
                const xTR_top = cxTR - t;   const yTR_right = cyTR + t;
                const yBR_right = cyBR - t; const xBR_bottom = cxBR - t;
                const xBL_bottom = cxBL + t; const yBL_left = cyBL - t;

                const dz = totalDescent / 8;

                // 起点定位：从中心移到左边BL左切点（仅首次，清根时跳过）
                if (!skipPositioning) {
                    nc += `G1 X${x1.toFixed(3)} Y${yBL_left.toFixed(3)} F${this.feed}\n`;
                }

                // 1. 左边上行: BL左切 → TL左切  (ΔX=0, ΔY=yTL_left - yBL_left)
                nc += `G1 Y${(yTL_left - yBL_left).toFixed(3)} Z${dz.toFixed(3)}\n`;

                // 2. 左上角 G3 CCW: TL左切 → TL上切
                nc += `G3 X${(xTL_top - x1).toFixed(3)} Y${(y2 - yTL_left).toFixed(3)} Z${dz.toFixed(3)} I${(cxTL - x1).toFixed(3)} J${(cyTL - yTL_left).toFixed(3)}\n`;

                // 3. 上边右行: TL上切 → TR上切  (ΔY=0)
                nc += `G1 X${(xTR_top - xTL_top).toFixed(3)} Z${dz.toFixed(3)}\n`;

                // 4. 右上角 G3 CCW: TR上切 → TR右切
                nc += `G3 X${(x2 - xTR_top).toFixed(3)} Y${(yTR_right - y2).toFixed(3)} Z${dz.toFixed(3)} I${(cxTR - xTR_top).toFixed(3)} J${(cyTR - y2).toFixed(3)}\n`;

                // 5. 右边下行: TR右切 → BR右切  (ΔX=0)
                nc += `G1 Y${(yBR_right - yTR_right).toFixed(3)} Z${dz.toFixed(3)}\n`;

                // 6. 右下角 G3 CCW: BR右切 → BR下切
                nc += `G3 X${(xBR_bottom - x2).toFixed(3)} Y${(y1 - yBR_right).toFixed(3)} Z${dz.toFixed(3)} I${(cxBR - x2).toFixed(3)} J${(cyBR - yBR_right).toFixed(3)}\n`;

                // 7. 下边左行: BR下切 → BL下切  (ΔY=0)
                nc += `G1 X${(xBL_bottom - xBR_bottom).toFixed(3)} Z${dz.toFixed(3)}\n`;

                // 8. 左下角 G3 CCW: BL下切 → BL左切
                nc += `G3 X${(x1 - xBL_bottom).toFixed(3)} Y${(yBL_left - y1).toFixed(3)} Z${dz.toFixed(3)} I${(cxBL - xBL_bottom).toFixed(3)} J${(cyBL - y1).toFixed(3)}\n`;
            } else {
                // 无圆角，4段直边
                const dz = totalDescent / 4;

                if (!skipPositioning) {
                    nc += `G1 X${x1.toFixed(3)} Y${y1.toFixed(3)} F${this.feed}\n`;
                }

                // 1. 左边上行: (x1,y1) → (x1,y2)  ΔX=0, ΔY=y2-y1
                nc += `G1 Y${(y2 - y1).toFixed(3)} Z${dz.toFixed(3)}\n`;
                // 2. 上边右行: (x1,y2) → (x2,y2)  ΔY=0
                nc += `G1 X${(x2 - x1).toFixed(3)} Z${dz.toFixed(3)}\n`;
                // 3. 右边下行: (x2,y2) → (x2,y1)  ΔX=0
                nc += `G1 Y${(y1 - y2).toFixed(3)} Z${dz.toFixed(3)}\n`;
                // 4. 下边左行: (x2,y1) → (x1,y1)  ΔY=0
                nc += `G1 X${(x1 - x2).toFixed(3)} Z${dz.toFixed(3)}\n`;
            }

            return nc;
        },

        // 底面平铣（回旋方式，不抬刀）
        generateFlatMill() {
            if (this.cavityType === "circular") {
                return this.generateFlatMillCircular();
            } else {
                return this.generateFlatMillSquare();
            }
        },

        // 圆形平铣：同心扩展圆（G3 4×90°整圆，G91相对坐标）
        generateFlatMillCircular() {
            const toolR = this.toolDiameter / 2;
            const maxR = this.diameter / 2 - toolR;
            if (maxR <= 0) return "";

            const step = this.actualCutDepth;
            let nc = "";
            let lastR = 0;

            for (let r = step; r <= maxR + 0.001; r += step) {
                lastR = Math.min(r, maxR);
                // 从中心(0,0)移到圆周起点(lastR, 0)
                nc += `G1 X${lastR.toFixed(3)} Y0\n`;
                // 弧1: (lastR,0) → (0,lastR)
                nc += `G3 X${(-lastR).toFixed(3)} Y${lastR.toFixed(3)} I${(-lastR).toFixed(3)} J0\n`;
                // 弧2: (0,lastR) → (-lastR,0)
                nc += `G3 X${(-lastR).toFixed(3)} Y${(-lastR).toFixed(3)} I0 J${(-lastR).toFixed(3)}\n`;
                // 弧3: (-lastR,0) → (0,-lastR)
                nc += `G3 X${lastR.toFixed(3)} Y${(-lastR).toFixed(3)} I${lastR.toFixed(3)} J0\n`;
                // 弧4: (0,-lastR) → (lastR,0)
                nc += `G3 X${lastR.toFixed(3)} Y${lastR.toFixed(3)} I0 J${lastR.toFixed(3)}\n`;
            }

            // 回到中心 (当前在lastR,0，移到0,0)
            nc += `G1 X${(-lastR).toFixed(3)} Y0\n`;
            return nc;
        },

        // 方形平铣：矩形扩展回旋（G91相对坐标）
        generateFlatMillSquare() {
            const toolR = this.toolDiameter / 2;
            const maxX = this.length / 2 - toolR;
            const maxY = this.width / 2 - toolR;

            if (maxX <= 0 || maxY <= 0) return "";

            const step = this.actualCutDepth;
            let nc = "";
            let cx = 0, cy = 0;  // 当前绝对位置（用于边界判断和增量计算）
            let legCount = 1;

            // 辅助：输出相对移动并更新当前绝对位置
            const g1 = (x, y) => {
                const dx = x - cx, dy = y - cy;
                cx = x; cy = y;
                if (Math.abs(dy) < 0.0005) return `G1 X${dx.toFixed(3)}\n`;
                if (Math.abs(dx) < 0.0005) return `G1 Y${dy.toFixed(3)}\n`;
                return `G1 X${dx.toFixed(3)} Y${dy.toFixed(3)}\n`;
            };

            // 矩形扩展回旋：右、上、左、下 交替，逐步扩大
            // eslint-disable-next-line no-constant-condition
            while (true) {
                // 右
                let nx = legCount * step;
                if (nx >= maxX) {
                    nc += g1(maxX, cy);
                    if (cy < maxY) nc += g1(maxX, maxY);
                    if (cx > -maxX) nc += g1(-maxX, cy);
                    if (cy > -maxY) nc += g1(cx, -maxY);
                    break;
                }
                nc += g1(nx, cy);

                // 上
                let ny = legCount * step;
                if (ny >= maxY) {
                    nc += g1(cx, maxY);
                    if (cx > -maxX) nc += g1(-maxX, cy);
                    if (cy > -maxY) nc += g1(cx, -maxY);
                    break;
                }
                nc += g1(cx, ny);
                legCount++;

                // 左
                nx = -legCount * step;
                if (nx <= -maxX) {
                    nc += g1(-maxX, cy);
                    if (cy > -maxY) nc += g1(cx, -maxY);
                    break;
                }
                nc += g1(nx, cy);

                // 下
                ny = -legCount * step;
                if (ny <= -maxY) {
                    nc += g1(cx, -maxY);
                    if (cx < maxX) nc += g1(maxX, cy);
                    break;
                }
                nc += g1(cx, ny);
                legCount++;
            }

            // 回到中心
            nc += g1(0, 0);
            return nc;
        },

        // 主生成函数
        generateNC() {
            // --- 验证 ---
            if (this.cavityType === "circular") {
                if (this.diameter <= 0) {
                    alert("直径必须大于0"); return false;
                }
                if (this.diameter <= this.toolDiameter) {
                    alert("直径必须大于刀具直径"); return false;
                }
            } else {
                if (this.length <= 0 || this.width <= 0) {
                    alert("长度和宽度必须大于0"); return false;
                }
                if (this.length <= this.toolDiameter || this.width <= this.toolDiameter) {
                    alert("长度和宽度必须大于刀具直径"); return false;
                }
                if (this.cornerRadius < 0) {
                    alert("角半径不能为负数"); return false;
                }
                if (this.cornerRadius > this.length / 2 || this.cornerRadius > this.width / 2) {
                    alert("角半径不能超过腔体尺寸的一半"); return false;
                }
            }
            if (this.stroke <= 0) {
                alert("深度必须大于0"); return false;
            }
            if (this.descentMode === "slope" && (this.slopeAngle <= 0 || this.slopeAngle >= 90)) {
                alert("坡度必须在0-90之间"); return false;
            }
            if (this.descentMode === "layerHeight" && this.layerHeight <= 0) {
                alert("每层高度必须大于0"); return false;
            }
            if (this.actualHeightPerRev <= 0) {
                alert("每转下降高度必须大于0，请检查坡度与腔体尺寸"); return false;
            }
            if (this.feed <= 0) {
                alert("进给必须大于0"); return false;
            }
            if (this.toolDiameter <= 0) {
                alert("刀具直径必须大于0"); return false;
            }
            if (this.safeZ <= 0) {
                alert("安全距离必须大于0"); return false;
            }
            if (this.topOffset < 0) {
                alert("顶偏置距离不能为负数"); return false;
            }

            // --- 文件头 ---
            const cavityLabel = this.cavityType === "circular" ? "圆内腔" : "方内腔";
            const now = new Date();
            const timestamp = now.getFullYear() + "-" +
                String(now.getMonth() + 1).padStart(2, "0") + "-" +
                String(now.getDate()).padStart(2, "0") + " " +
                String(now.getHours()).padStart(2, "0") + ":" +
                String(now.getMinutes()).padStart(2, "0") + ":" +
                String(now.getSeconds()).padStart(2, "0");

            let nc = `(螺纹铣 - ${cavityLabel}  生成时间: ${timestamp})\n`;
            nc += `(当前位置=腔体中心，G91相对坐标，结束后返回起点)\n`;
            nc += `(z=0 为当前刀尖位置，顶偏置=${this.topOffset.toFixed(1)}mm)\n`;
            nc += `(坡度=${this.actualSlopeAngle.toFixed(2)}°, 周长=${this.perimeter.toFixed(2)}mm, 每转下降=${this.actualHeightPerRev.toFixed(4)}mm)\n`;
            if (this.needsFlatMill) {
                nc += `(底面平铣: 回旋方式, 步进=${this.actualCutDepth.toFixed(2)}mm)\n`;
            }
            nc += `%\n`;
            nc += `G91 G54\n`;
            nc += `G0 Z${this.safeZ.toFixed(3)}\n`;
            nc += `S${this.spindleRPM} M03\n`;
            // 当前位置即中心，无需G0 X0 Y0
            nc += `G1 Z${(this.topOffset - this.safeZ).toFixed(3)} F${this.feed}\n`;

            // --- 逐层螺旋下降 ---
            // 当前在中心(0,0)，Z=topOffset（相对起始位置上方）
            const targetZ = -this.stroke;
            let currentZ = this.topOffset;
            const heightPerRev = this.actualHeightPerRev;
            const toolR = this.toolDiameter / 2;
            const pathR = this.cavityType === "circular" ? this.diameter / 2 - toolR : 0;

            // 对于圆形：先走到圆周起点（之后始终保持在圆周上）
            if (this.cavityType === "circular") {
                nc += `G1 X${pathR.toFixed(3)} Y0\n`;
            }

            while (currentZ > targetZ + 0.001) {
                let nextZ = currentZ - heightPerRev;
                if (nextZ < targetZ) nextZ = targetZ;

                // 平铣当前层（不抬刀）
                if (this.needsFlatMill) {
                    // 回到中心：刀具在螺旋路径起点，需反向走回中心
                    if (this.cavityType === "circular") {
                        nc += `G1 X${(-pathR).toFixed(3)} Y0\n`;
                    } else {
                        // 方形：螺旋结束于路径起点，计算该点相对于中心的偏移
                        const sx1 = -this.length / 2 + toolR;
                        if (this.cornerRadius > 0) {
                            const t = 2 * Math.sqrt(this.cornerRadius * toolR);
                            const cyBL = -this.width / 2 + this.cornerRadius;
                            const syBL = cyBL - t;  // 左下角左切点Y
                            nc += `G1 X${(-sx1).toFixed(3)} Y${(-syBL).toFixed(3)}\n`;
                        } else {
                            const sy1 = -this.width / 2 + toolR;
                            nc += `G1 X${(-sx1).toFixed(3)} Y${(-sy1).toFixed(3)}\n`;
                        }
                    }
                    nc += this.generateFlatMill(); // 平铣，结束时回到中心
                    // 回到圆周起点
                    if (this.cavityType === "circular") {
                        nc += `G1 X${pathR.toFixed(3)} Y0\n`;
                    }
                    // 方形由 generateSquareHelical 内部定位
                }

                // 螺旋下降一圈
                if (this.cavityType === "circular") {
                    nc += this.generateCircularHelical(currentZ, nextZ);
                } else {
                    nc += this.generateSquareHelical(currentZ, nextZ);
                }

                currentZ = nextZ;
            }

            // --- 底部清根：沿腔壁走一圈（Z不变） ---
            if (this.cavityType === "circular") {
                nc += this.generateCircularCleanup();
            } else {
                // 方形：走一圈CCW周边（无Z变化），跳过定位（刀具已在起点）
                nc += this.generateSquareHelical(currentZ, currentZ, true);
            }

            // --- 收尾：返回起点XYZ ---
            // 当前Z = targetZ（-stroke），先抬刀至安全高度，再XY回中心，最后Z回起点
            nc += `(返回起点)\n`;
            nc += `G0 Z${(this.safeZ - currentZ).toFixed(3)}\n`;
            // XY回中心
            if (this.cavityType === "circular") {
                nc += `G0 X${(-pathR).toFixed(3)} Y0\n`;
            } else {
                const x1 = -this.length / 2 + toolR;
                const y1 = -this.width / 2 + toolR;
                if (this.cornerRadius > 0) {
                    const t = 2 * Math.sqrt(this.cornerRadius * toolR);
                    const cyBL = -this.width / 2 + this.cornerRadius;
                    const yBL_left = cyBL - t;
                    nc += `G0 X${(-x1).toFixed(3)} Y${(-yBL_left).toFixed(3)}\n`;
                } else {
                    nc += `G0 X${(-x1).toFixed(3)} Y${(-y1).toFixed(3)}\n`;
                }
            }
            // Z回到起始高度
            nc += `G0 Z${(-this.safeZ).toFixed(3)}\n`;
            nc += `M05\n`;
            nc += `%\n`;

            // --- 输出 ---
            const dimLabel = this.cavityType === "circular"
                ? `D${this.diameter.toFixed(1)}`
                : `${this.length.toFixed(1)}x${this.width.toFixed(1)}`;
            const filename = `螺纹铣-${cavityLabel}-${dimLabel}-深${this.stroke.toFixed(1)}.nc`;
            this.fileManager.addFileTab(filename, nc);

            return true;
        },
    },
};
</script>
